import { IssueSeverity } from '@/api/generated-types';
import { SEVERITY_RANK } from '@/config/fieldConfig/severity';
import type { RowErrors, CtuBreakdownEntry } from '@/stores/validationStore';

type BreakdownField = {
  field: string;
  label: string;
  severity: IssueSeverity;
  message?: string | null;
};

function fieldsToRow(fields: readonly BreakdownField[] | undefined): RowErrors {
  const row: RowErrors = {};
  for (const f of fields ?? []) {
    const existing = row[f.field];
    // Highest severity wins per field — the breakdown can carry more than one issue for a field,
    // e.g. an extraction gap Warning + a validator Error.
    if (!existing || SEVERITY_RANK[f.severity] < SEVERITY_RANK[existing.severity]) {
      row[f.field] = { label: f.label, message: f.message ?? '', severity: f.severity };
    }
  }
  return row;
}

/**
 * Derive the per-cargo and per-CTU error lookup maps from the breakdown tree — the single
 * authoritative findings shape after the cargoErrors/ctuErrors dictionaries were dropped.
 * Single O(n) pass over entries → items → fields.
 */
export function deriveErrorMapsFromBreakdown(
  breakdown: readonly CtuBreakdownEntry[] | null | undefined
): { cargoErrors: Map<string, RowErrors>; ctuErrors: Map<string, RowErrors> } {
  const cargoErrors = new Map<string, RowErrors>();
  const ctuErrors = new Map<string, RowErrors>();

  for (const entry of breakdown ?? []) {
    if (entry.ctuFields?.length) {
      ctuErrors.set(entry.ctuId, fieldsToRow(entry.ctuFields));
    }
    for (const item of entry.items ?? []) {
      cargoErrors.set(item.cargoId, fieldsToRow(item.fields));
    }
  }

  return { cargoErrors, ctuErrors };
}

/** Extract the CTU array index from a backend validation error property path. */
export function parseCtuIndexFromProperty(property: string | undefined): number | null {
  if (!property) return null;
  const match = property.match(/CargoTransportUnits\[(\d+)\]/);
  return match ? parseInt(match[1], 10) : null;
}

/** Convert a backend PascalCase property path to a frontend camelCase fieldPath. */
export function backendPropertyToFieldPath(property: string): string {
  return property
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((seg) => seg.charAt(0).toLowerCase() + seg.slice(1))
    .join('.');
}
